import type { Manejo, Sexo } from '../dados/fonte.js';
import { CampoNumero } from './CampoNumero.js';
import { Campo, classeCampo, OpcoesRadio } from './ui/index.js';

type Salvar = (m: Partial<Manejo>) => Promise<void>;

const simNao = [
  { valor: 'sim', rotulo: 'Sim' },
  { valor: 'nao', rotulo: 'Não' },
] as const;
const deBool = (v: boolean | null) => (v == null ? '' : v ? 'sim' : 'nao');

const PATAS = [
  { campo: 'shod_fl', rotulo: 'AE' },
  { campo: 'shod_fr', rotulo: 'AD' },
  { campo: 'shod_hl', rotulo: 'PE' },
  { campo: 'shod_hr', rotulo: 'PD' },
] as const;

/**
 * Estacao 1 alem de peso e ECC: o que a ficha completa (Nivel 2) pergunta
 * sobre alimentacao, reproducao, treino, ferrageamento, aptidao e DNA. Cada
 * campo grava na hora; nada aqui e obrigatorio pelo protocolo, mas as regras
 * PRD-001/003/004 olham para estes valores.
 */
export function ManejoCompleto({
  manejo: m,
  sexo,
  desabilitado,
  salvar,
}: {
  manejo: Manejo;
  sexo: Sexo;
  desabilitado: boolean;
  salvar: Salvar;
}) {
  const gravar = (parcial: Partial<Manejo>) => {
    if (!desabilitado) void salvar(parcial).catch(() => undefined);
  };
  const semAptidao = m.fitness === 'claudicacao' || m.fitness === 'lesao';

  return (
    <div className="space-y-6 border-t border-borda pt-5">
      <section className="grid gap-5 sm:grid-cols-3">
        <Campo rotulo="Última alimentação">
          {(a) => (
            <input
              {...a}
              type="time"
              disabled={desabilitado}
              defaultValue={m.last_feed_time?.slice(0, 5) ?? ''}
              onBlur={(e) => gravar({ last_feed_time: e.target.value || null })}
              className={`${classeCampo()} max-w-36`}
            />
          )}
        </Campo>
        <OpcoesRadio
          rotulo="Água"
          valor={m.water_access ?? ''}
          onMudar={(v) => gravar({ water_access: v })}
          opcoes={[
            { valor: 'livre', rotulo: 'Livre' },
            { valor: 'restrita', rotulo: 'Restrita' },
          ]}
        />
        <OpcoesRadio
          rotulo="No haras há mais de 30 dias"
          ajuda="Chegada recente: só Nível 1."
          valor={deBool(m.arrived_farm_gt_30d)}
          onMudar={(v) => gravar({ arrived_farm_gt_30d: v === 'sim' })}
          opcoes={simNao}
        />
      </section>

      {sexo === 'femea' && (
        <section className="grid gap-5 sm:grid-cols-3">
          <div className="sm:col-span-3">
            <OpcoesRadio
              rotulo="Situação reprodutiva"
              valor={m.female_status ?? ''}
              onMudar={(v) => gravar({ female_status: v })}
              opcoes={[
                { valor: 'vazia', rotulo: 'Vazia' },
                { valor: 'prenhe', rotulo: 'Prenhe' },
                { valor: 'parida', rotulo: 'Parida' },
                { valor: 'lactando', rotulo: 'Lactando' },
                { valor: 'doadora', rotulo: 'Doadora' },
              ]}
            />
          </div>
          {m.female_status === 'prenhe' && (
            <CampoNumero
              rotulo="Meses de gestação"
              valor={m.gestation_months}
              casas={1}
              unidade="meses"
              desabilitado={desabilitado}
              onSalvar={(v) => salvar({ gestation_months: v })}
            />
          )}
          {(m.female_status === 'parida' || m.female_status === 'lactando') && (
            <CampoNumero
              rotulo="Dias pós-parto"
              valor={m.days_postpartum}
              casas={0}
              unidade="dias"
              desabilitado={desabilitado}
              onSalvar={(v) => salvar({ days_postpartum: v })}
            />
          )}
        </section>
      )}

      {sexo === 'macho_inteiro' && (
        <section className="grid gap-5 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <OpcoesRadio
              rotulo="Reprodução"
              valor={m.male_status ?? ''}
              onMudar={(v) => gravar({ male_status: v })}
              opcoes={[
                { valor: 'em_reproducao', rotulo: 'Em reprodução' },
                { valor: 'fora_de_reproducao', rotulo: 'Fora de reprodução' },
              ]}
            />
          </div>
          {m.male_status === 'em_reproducao' && (
            <CampoNumero
              rotulo="Coberturas na última semana"
              valor={m.covers_last_week}
              casas={0}
              unidade=""
              desabilitado={desabilitado}
              onSalvar={(v) => salvar({ covers_last_week: v })}
            />
          )}
        </section>
      )}

      <section className="grid gap-5 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <OpcoesRadio
            rotulo="Treinamento"
            valor={m.training_status ?? ''}
            onMudar={(v) => gravar({ training_status: v })}
            opcoes={[
              { valor: 'sem_trabalho', rotulo: 'Sem trabalho' },
              { valor: 'em_doma', rotulo: 'Em doma' },
              { valor: 'em_trabalho', rotulo: 'Em trabalho' },
              { valor: 'preparo_exposicao', rotulo: 'Preparo p/ exposição' },
            ]}
          />
        </div>
        {m.training_status && m.training_status !== 'sem_trabalho' && (
          <CampoNumero
            rotulo="Tempo de treino"
            valor={m.training_months}
            casas={1}
            unidade="meses"
            desabilitado={desabilitado}
            onSalvar={(v) => salvar({ training_months: v })}
          />
        )}
      </section>

      <section className="grid gap-5 sm:grid-cols-3">
        <Campo rotulo="Último ferrageamento">
          {(a) => (
            <input
              {...a}
              type="date"
              disabled={desabilitado}
              defaultValue={m.last_shoeing_date ?? ''}
              onBlur={(e) => gravar({ last_shoeing_date: e.target.value || null })}
              className={`${classeCampo()} max-w-44`}
            />
          )}
        </Campo>
        <fieldset className="space-y-1.5">
          <legend className="mb-1.5 text-sm font-medium">Ferrado</legend>
          <div className="flex flex-wrap gap-1">
            {PATAS.map(({ campo, rotulo }) => (
              <button
                key={campo}
                type="button"
                aria-pressed={m[campo]}
                disabled={desabilitado}
                onClick={() => gravar({ [campo]: !m[campo], ...(!m[campo] ? { barefoot: false } : {}) })}
                className={`min-h-11 min-w-11 rounded-(--radius-controle) border px-2 text-sm font-medium disabled:opacity-60 ${
                  m[campo] ? 'border-marca bg-marca text-sobre-marca' : 'border-borda-forte text-apagado'
                }`}
              >
                {rotulo}
              </button>
            ))}
            <button
              type="button"
              aria-pressed={m.barefoot}
              disabled={desabilitado}
              onClick={() =>
                gravar(m.barefoot ? { barefoot: false } : { barefoot: true, shod_fl: false, shod_fr: false, shod_hl: false, shod_hr: false })
              }
              className={`min-h-11 rounded-(--radius-controle) border px-3 text-sm font-medium disabled:opacity-60 ${
                m.barefoot ? 'border-marca bg-marca text-sobre-marca' : 'border-borda-forte text-apagado'
              }`}
            >
              Descalço
            </button>
          </div>
        </fieldset>
        <OpcoesRadio
          rotulo="Cascos"
          valor={m.hoof_condition ?? ''}
          onMudar={(v) => gravar({ hoof_condition: v })}
          opcoes={[
            { valor: 'normais', rotulo: 'Normais' },
            { valor: 'achinelados', rotulo: 'Achinelados' },
            { valor: 'encastelados', rotulo: 'Encastelados' },
          ]}
        />
      </section>

      <section className="grid gap-5 sm:grid-cols-3">
        <OpcoesRadio
          rotulo="Aptidão"
          valor={m.fitness ?? ''}
          onMudar={(v) => gravar(v === 'apto' ? { fitness: v, lameness_limb: null, lameness_aaep: 0 } : { fitness: v })}
          opcoes={[
            { valor: 'apto', rotulo: 'Apto' },
            { valor: 'claudicacao', rotulo: 'Claudicação' },
            { valor: 'lesao', rotulo: 'Lesão' },
          ]}
        />
        {semAptidao && (
          <>
            <OpcoesRadio
              rotulo="Membro"
              valor={m.lameness_limb ?? ''}
              onMudar={(v) => gravar({ lameness_limb: v })}
              opcoes={[
                { valor: 'AE', rotulo: 'AE' },
                { valor: 'AD', rotulo: 'AD' },
                { valor: 'PE', rotulo: 'PE' },
                { valor: 'PD', rotulo: 'PD' },
              ]}
            />
            <OpcoesRadio
              rotulo="Grau AAEP"
              ajuda="Grau 1 ou mais: sem Estação 4."
              valor={m.lameness_aaep == null ? '' : String(m.lameness_aaep)}
              onMudar={(v) => gravar({ lameness_aaep: Number(v) })}
              opcoes={['0', '1', '2', '3', '4', '5'].map((n) => ({ valor: n, rotulo: n }))}
            />
          </>
        )}
      </section>

      <section className="grid gap-5 sm:grid-cols-3">
        <OpcoesRadio
          rotulo="Pelo coletado para DNA"
          valor={m.dna_sample_collected ? 'sim' : 'nao'}
          onMudar={(v) => gravar({ dna_sample_collected: v === 'sim' })}
          opcoes={simNao}
        />
        {m.dna_sample_collected && (
          <Campo rotulo="Nº do tubo">
            {(a) => (
              <input
                {...a}
                type="text"
                disabled={desabilitado}
                defaultValue={m.dna_tube_id ?? ''}
                onBlur={(e) => gravar({ dna_tube_id: e.target.value.trim() || null })}
                className={`${classeCampo()} max-w-44 font-mono`}
              />
            )}
          </Campo>
        )}
      </section>
    </div>
  );
}
